/**
 * FormWizard — schema-driven client-questionnaire wizard.
 *
 * Single component that renders any client-facing schema-driven form by
 * loading the canonical schema from /api/{formType}/schema and walking
 * through sections one step at a time.  Used by:
 *
 *   - TA6IntakeForm       (formType="ta6")
 *   - TA7IntakeForm       (formType="ta7")
 *   - SourceOfFundsForm   (formType="sof")
 *
 * Question kinds (closed vocabulary):
 *   yn        Yes / No
 *   ynnk      Yes / No / Not known
 *   ynu       Yes / No / Unsure
 *   text      free text (single line)
 *   textarea  free text (multi-line)
 *   number    numeric
 *   currency  £ amount (number with currency prefix)
 *   date      DD/MM/YYYY
 *   year      YYYY
 *   choices   pick one of a fixed list
 *   multi     pick zero or more of a fixed list
 *   cash_band <£3,000 / £3,000–£9,999 / £10,000 or more
 *   checkbox  declaration ack (boolean)
 *   signature signature canvas → PNG dataURL
 *
 * Conditional visibility:
 *   - Section-level `show_if_source` — gates SoF §2-§9 on §1.11 picks.
 *   - Question-level `show_if` — { field, equals|in|includes }.
 *
 * Standing rules:
 *   - declaration_confirmed + declaration_signed_by are the keys the
 *     backend submit gate looks for.  Locked by frontend invariant.
 *   - Every interactive control has a data-testid.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import {
    ChevronLeft, ChevronRight, CheckCircle, Loader2, Download,
    FileText, AlertTriangle, Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import "../styles/client-forms.css";
import "./TAFormWizard.css";
import { LOGO_URL, FIRM_NAME } from "../config/constants";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Form metadata per type ────────────────────────────────────────

const FORM_META = {
    ta6: {
        titleFmt: (edition) => `TA6 Property Information Form — ${edition}`,
        label: "TA6",
    },
    ta7: {
        titleFmt: (edition) => `TA7 Leasehold Information Form — ${edition}`,
        label: "TA7",
    },
    sof: {
        titleFmt: (edition) => `Source of Funds & Source of Wealth Questionnaire — ${edition}`,
        label: "SoF",
    },
};

// ── Conditional visibility evaluator (mirrors backend should_show) ─

function shouldShowQuestion(q, formData) {
    const cond = q.show_if;
    if (!cond) return true;
    const v = formData[cond.field];
    if ("equals" in cond) return v === cond.equals;
    if ("in" in cond) return Array.isArray(cond.in) && cond.in.includes(v);
    if ("includes" in cond) return Array.isArray(v) && v.includes(cond.includes);
    return true;
}

function shouldShowSection(section, formData) {
    const gate = section.show_if_source;
    if (!gate) return true;
    const applicable = formData["s1_applicable_sources"] || [];
    return Array.isArray(applicable) && applicable.includes(gate);
}

// ── Allocation reconciliation gate (SoF §10) ──────────────────────

const ALLOC_SPLIT_KEYS = [
    "s10_split_savings", "s10_split_gift", "s10_split_mortgage",
    "s10_split_property_sale", "s10_split_isa", "s10_split_divorce",
    "s10_split_inheritance", "s10_split_other",
];

function checkAllocation(formData) {
    const share = parseFloat(formData["s10_share_of_contribution"] || 0);
    if (!share) return { ok: true, share: 0, total: 0 };
    const total = ALLOC_SPLIT_KEYS.reduce(
        (acc, k) => acc + parseFloat(formData[k] || 0), 0);
    return { ok: Math.abs(total - share) <= 500, share, total };
}

// ── Tiny presentational primitives ────────────────────────────────

function Tristate({ id, value, onChange, options }) {
    return (
        <div className="flex gap-2 flex-wrap" data-testid={`field-${id}`}>
            {options.map(opt => (
                <button
                    key={opt}
                    type="button"
                    onClick={() => onChange(opt)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        value === opt
                            ? "bg-teal/30 border-teal text-teal"
                            : "border-white/10 text-slate-400 hover:border-white/25"
                    }`}
                    data-testid={`${id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`}
                >
                    {opt}
                </button>
            ))}
        </div>
    );
}

function ChoicesButtons({ id, value, choices, onChange, multi }) {
    const selected = multi
        ? (Array.isArray(value) ? value : [])
        : (value ? [value] : []);
    const toggle = (c) => {
        if (multi) {
            onChange(selected.includes(c)
                ? selected.filter(x => x !== c)
                : [...selected, c]);
        } else {
            onChange(c);
        }
    };
    return (
        <div className="flex gap-2 flex-wrap" data-testid={`field-${id}`}>
            {choices.map(c => (
                <button
                    key={c}
                    type="button"
                    onClick={() => toggle(c)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        selected.includes(c)
                            ? "bg-teal/30 border-teal text-teal"
                            : "border-white/10 text-slate-400 hover:border-white/25"
                    }`}
                    data-testid={`${id}-${c.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`}
                >
                    {c}
                </button>
            ))}
        </div>
    );
}

function CurrencyInput({ id, value, onChange }) {
    return (
        <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">£</span>
            <input
                type="number"
                min="0"
                step="0.01"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="ta-form-input w-40"
                data-testid={`input-${id}`}
            />
        </div>
    );
}

function CheckboxField({ id, value, onChange, label }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer" data-testid={`field-${id}`}>
            <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1"
                data-testid={`input-${id}`}
            />
            <span className="text-sm text-slate-200">{label}</span>
        </label>
    );
}

function HelpTooltip({ text }) {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    return (
        <div className="mt-1">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="inline-flex items-center gap-1 text-xs text-teal/80 hover:text-teal"
                data-testid="question-help-toggle"
            >
                <Info className="w-3 h-3" />
                {open ? "Hide guidance" : "Show guidance"}
            </button>
            {open && (
                <p className="text-xs text-slate-400 italic mt-1 leading-relaxed whitespace-pre-line">
                    {text}
                </p>
            )}
        </div>
    );
}

function QuestionRow({ q, value, onChange }) {
    const id = q.key;
    const v = value;

    // Checkbox renders inline (the question text IS the label)
    if (q.kind === "checkbox") {
        return (
            <div className="py-2 border-b border-white/5 last:border-b-0"
                 data-testid={`question-${id}`}>
                <CheckboxField id={id} value={v} onChange={(x) => onChange(id, x)} label={q.text} />
                <HelpTooltip text={q.help_text} />
            </div>
        );
    }

    return (
        <div className="space-y-1.5 py-2 border-b border-white/5 last:border-b-0"
             data-testid={`question-${id}`}>
            <label className="text-sm text-slate-200 leading-snug block">
                {q.text}
                {q.required && <span className="text-status-red ml-1">*</span>}
            </label>
            <HelpTooltip text={q.help_text} />
            {(() => {
                switch (q.kind) {
                    case "yn":
                        return <Tristate id={id} value={v} options={["Yes", "No"]} onChange={(x) => onChange(id, x)} />;
                    case "ynnk":
                        return <Tristate id={id} value={v} options={["Yes", "No", "Not known"]} onChange={(x) => onChange(id, x)} />;
                    case "ynu":
                        return <Tristate id={id} value={v} options={["Yes", "No", "Unsure"]} onChange={(x) => onChange(id, x)} />;
                    case "choices":
                        return <ChoicesButtons id={id} value={v} choices={q.choices || []} onChange={(x) => onChange(id, x)} />;
                    case "cash_band":
                        return <ChoicesButtons id={id} value={v} choices={q.choices || []} onChange={(x) => onChange(id, x)} />;
                    case "multi":
                        return <ChoicesButtons id={id} value={v} choices={q.choices || []} onChange={(x) => onChange(id, x)} multi />;
                    case "date":
                        return <input type="date" value={v || ""}
                                      onChange={(e) => onChange(id, e.target.value)}
                                      className="ta-form-input" data-testid={`input-${id}`} />;
                    case "year":
                        return <input type="number" min="1800" max="2100" placeholder="YYYY"
                                      value={v || ""}
                                      onChange={(e) => onChange(id, e.target.value)}
                                      className="ta-form-input w-32" data-testid={`input-${id}`} />;
                    case "number":
                        return <input type="number" value={v || ""}
                                      onChange={(e) => onChange(id, e.target.value)}
                                      className="ta-form-input w-40" data-testid={`input-${id}`} />;
                    case "currency":
                        return <CurrencyInput id={id} value={v} onChange={(x) => onChange(id, x)} />;
                    case "textarea":
                        return <textarea rows={3} value={v || ""}
                                         onChange={(e) => onChange(id, e.target.value)}
                                         className="ta-form-textarea w-full"
                                         data-testid={`input-${id}`} />;
                    case "signature":
                        // Lightweight placeholder — typed signature in declaration is the
                        // legal signing field; the canvas is optional belt-and-braces.
                        return <input type="text" value={v || ""}
                                      placeholder="(Optional — typed name above is your legal signature)"
                                      onChange={(e) => onChange(id, e.target.value)}
                                      className="ta-form-input w-full" data-testid={`input-${id}`} />;
                    case "text":
                    default:
                        return <input type="text" value={v || ""}
                                      onChange={(e) => onChange(id, e.target.value)}
                                      className="ta-form-input w-full"
                                      data-testid={`input-${id}`} />;
                }
            })()}
        </div>
    );
}

// ── Main wizard ───────────────────────────────────────────────────

export default function FormWizard({ formType }) {
    const { token } = useParams();
    const apiBase = `${API}/api/${formType}`;
    const meta_block = FORM_META[formType] || FORM_META.ta6;

    const [schema, setSchema] = useState(null);
    const [meta, setMeta] = useState(null);
    const [formData, setFormData] = useState({});
    const [stepIdx, setStepIdx] = useState(0);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [s, m] = await Promise.all([
                    // BM-H604: the token-authorised schema. GET /schema takes
                    // no token, is sealed behind the staff session, and its
                    // 401 sent clients to the staff Microsoft login.
                    axios.get(`${apiBase}/${token}/schema`),
                    axios.get(`${apiBase}/${token}`),
                ]);
                setSchema(s.data);
                setMeta(m.data);
                setFormData(m.data.form_data || {});
                setStepIdx(m.data.current_step || 0);
            } catch (e) {
                setError(errorMessage(e, "Could not load this form."));
            } finally {
                setLoading(false);
            }
        })();
    }, [apiBase, token]);

    // ── Section filtering (drop hidden sections from the step sequence) ──
    const visibleSections = useMemo(() => {
        if (!schema) return [];
        return (schema.sections || []).filter(s => shouldShowSection(s, formData));
    }, [schema, formData]);

    const updateField = useCallback((key, value) => {
        setFormData(prev => {
            const next = { ...prev, [key]: value };
            // Auto-confirm declaration when the 3 SoF checkboxes are all ticked
            if (formType === "sof" && (
                key === "s15_privacy_notice_ack" ||
                key === "s15_declaration_truthful" ||
                key === "s15_declaration_ongoing"
            )) {
                next.declaration_confirmed = !!(
                    next.s15_privacy_notice_ack &&
                    next.s15_declaration_truthful &&
                    next.s15_declaration_ongoing
                );
            }
            return next;
        });
    }, [formType]);

    const save = useCallback(async (newStep) => {
        if (!meta || meta.status === "submitted") return;
        setSaving(true);
        try {
            await axios.post(`${apiBase}/${token}/save`,
                { form_data: formData, current_step: newStep ?? stepIdx });
        } catch (e) {
            setError(errorMessage(e, "Could not save your progress."));
        } finally {
            setSaving(false);
        }
    }, [apiBase, token, formData, stepIdx, meta]);

    const submitForm = useCallback(async () => {
        // Allocation reconciliation gate (SoF only)
        if (formType === "sof") {
            const alloc = checkAllocation(formData);
            if (!alloc.ok) {
                setError(
                    `Funding allocation totals £${alloc.total.toLocaleString()} but ` +
                    `your contribution is £${alloc.share.toLocaleString()} — ` +
                    `please correct before submitting.`
                );
                return;
            }
        }
        if (!formData.declaration_confirmed || !(formData.declaration_signed_by || "").trim()) {
            setError("Please confirm the declaration and sign with your full name before submitting.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${apiBase}/${token}/submit`, { form_data: formData });
            const m = await axios.get(`${apiBase}/${token}`);
            setMeta(m.data);
        } catch (e) {
            setError(errorMessage(e, "Could not submit the form."));
        } finally {
            setSubmitting(false);
        }
    }, [apiBase, token, formData, formType]);

    const downloadPdf = useCallback(() => {
        window.open(`${apiBase}/${token}/pdf`, "_blank");
    }, [apiBase, token]);

    if (loading) {
        return (
            <div className="ta-form-wrapper" data-testid="ta-form-loading">
                <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" />
            </div>
        );
    }
    if (error && !schema) {
        return (
            <div className="ta-form-wrapper" data-testid="ta-form-error">
                <AlertTriangle className="w-8 h-8 text-status-red mx-auto mb-2" />
                <p className="text-center text-slate-300">{error}</p>
            </div>
        );
    }

    const submitted = meta?.status === "submitted";
    const sections = visibleSections;
    const totalSteps = sections.length;  // SoF includes declaration as the last section already
    const isLastStep = stepIdx >= totalSteps - 1;
    const isDeclarationStepForTA = formType !== "sof" && stepIdx >= sections.length;
    const allStepsCount = formType === "sof" ? totalSteps : sections.length + 1;
    const declarationOnTA = formType !== "sof" && isDeclarationStepForTA;
    const currentSection = declarationOnTA ? null : sections[stepIdx];

    const goPrev = async () => {
        await save(Math.max(0, stepIdx - 1));
        setStepIdx(s => Math.max(0, s - 1));
    };
    const goNext = async () => {
        await save(Math.min(allStepsCount - 1, stepIdx + 1));
        setStepIdx(s => Math.min(allStepsCount - 1, s + 1));
    };

    const formTitle = meta_block.titleFmt(schema.edition);
    const labelType = meta_block.label;
    const allocation = formType === "sof" ? checkAllocation(formData) : { ok: true };

    return (
        <div className="ta-form-wrapper" data-testid={`ta-form-${formType}`}>
            <img src={LOGO_URL} alt={FIRM_NAME} className="ta-form-logo" />
            <div className="ta-form-card">
                <header className="ta-form-header">
                    <div>
                        <h1 className="text-xl font-bold text-white" data-testid="ta-form-title">
                            <FileText className="w-5 h-5 text-teal inline mr-2" />
                            {formTitle}
                        </h1>
                        <p className="text-xs text-slate-500 mt-1">
                            Matter <span className="font-mono">{meta?.matter_reference}</span>
                            {meta?.client_name && <> · {meta.client_name}</>}
                        </p>
                    </div>
                    {submitted && (
                        <div className="flex items-center gap-2 text-status-green text-sm"
                             data-testid="ta-form-submitted-badge">
                            <CheckCircle className="w-4 h-4" /> Submitted
                        </div>
                    )}
                </header>

                <div className="ta-form-progress" data-testid={`ta-form-step-${stepIdx}`}>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Step {stepIdx + 1} of {allStepsCount}</span>
                        <span>{Math.round((stepIdx / Math.max(allStepsCount - 1, 1)) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-teal transition-all"
                             style={{ width: `${(stepIdx / Math.max(allStepsCount - 1, 1)) * 100}%` }} />
                    </div>
                </div>

                <div className="ta-form-body">
                    {!declarationOnTA && currentSection && (
                        <section data-testid={`section-${currentSection.number}`}>
                            <h2 className="text-base font-semibold text-teal mb-3">
                                {currentSection.number}. {currentSection.title}
                            </h2>
                            <div className="space-y-1">
                                {currentSection.questions
                                    .filter(q => shouldShowQuestion(q, formData))
                                    .map(q => (
                                        <QuestionRow
                                            key={q.key}
                                            q={q}
                                            value={formData[q.key]}
                                            onChange={updateField}
                                        />
                                    ))}
                            </div>

                            {/* SoF allocation reconciliation gate banner */}
                            {formType === "sof" && currentSection.number === 10 && !allocation.ok && (
                                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md"
                                     data-testid="allocation-mismatch-banner">
                                    <p className="text-xs text-amber-300">
                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                        Funding allocation totals <strong>£{allocation.total.toLocaleString()}</strong>{" "}
                                        but your contribution is <strong>£{allocation.share.toLocaleString()}</strong>{" "}
                                        — please correct before submitting.
                                    </p>
                                </div>
                            )}
                        </section>
                    )}
                    {declarationOnTA && (
                        <section data-testid="section-declaration">
                            <h2 className="text-base font-semibold text-teal mb-3">Declaration</h2>
                            <p className="text-sm text-slate-300 mb-4">
                                I confirm that the information I have provided in this {labelType} form
                                is truthful and complete to the best of my knowledge.
                            </p>
                            <div className="space-y-3">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox"
                                           checked={!!formData.declaration_confirmed}
                                           onChange={(e) => updateField("declaration_confirmed", e.target.checked)}
                                           data-testid="input-declaration_confirmed"
                                           className="mt-1" />
                                    <span className="text-sm text-slate-200">
                                        I confirm the declaration above.
                                    </span>
                                </label>
                                <div>
                                    <label className="text-sm text-slate-300 block mb-1">Full name *</label>
                                    <input type="text"
                                           value={formData.declaration_signed_by || ""}
                                           onChange={(e) => updateField("declaration_signed_by", e.target.value)}
                                           className="ta-form-input w-full"
                                           placeholder="Type your full legal name"
                                           data-testid="input-declaration_signed_by" />
                                </div>
                                <div>
                                    <label className="text-sm text-slate-300 block mb-1">Date</label>
                                    <input type="date"
                                           value={formData.declaration_date || ""}
                                           onChange={(e) => updateField("declaration_date", e.target.value)}
                                           className="ta-form-input"
                                           data-testid="input-declaration_date" />
                                </div>
                            </div>
                        </section>
                    )}
                    {error && (
                        <p className="mt-4 text-sm text-status-red" data-testid="ta-form-error-msg">
                            {error}
                        </p>
                    )}
                </div>

                <footer className="ta-form-footer">
                    <div className="flex justify-between items-center gap-2">
                        <Button variant="outline" size="sm"
                                disabled={stepIdx === 0 || saving || submitting}
                                onClick={goPrev}
                                data-testid="ta-form-back-btn">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Back
                        </Button>

                        <div className="text-xs text-slate-500">
                            {saving && <Loader2 className="w-3 h-3 inline animate-spin mr-1" />}
                            {saving ? "Saving…" : meta?.last_saved_at ? "Saved" : ""}
                        </div>

                        <div className="flex gap-2">
                            {submitted && (
                                <Button variant="outline" size="sm" onClick={downloadPdf}
                                        data-testid="ta-form-download-pdf-btn">
                                    <Download className="w-4 h-4 mr-1" /> Download PDF
                                </Button>
                            )}
                            {!isLastStep ? (
                                <Button size="sm" onClick={goNext}
                                        disabled={saving || submitted}
                                        data-testid="ta-form-next-btn">
                                    Next <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            ) : (
                                <Button size="sm" onClick={submitForm}
                                        disabled={submitting || submitted}
                                        data-testid="ta-form-submit-btn">
                                    {submitting ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</>) :
                                                  (<><CheckCircle className="w-4 h-4 mr-1" /> Submit form</>)}
                                </Button>
                            )}
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}

// Export helpers for the Node invariant harness
export { shouldShowQuestion, shouldShowSection, checkAllocation, ALLOC_SPLIT_KEYS };
